import { Head, Link } from "@inertiajs/react"
import { PublicShell } from "@/components/PublicShell"
import { Music2, Users, Disc3, CalendarDays, MapPin, BarChart3, User, ExternalLink } from "lucide-react"

type Image = { url: string; alt_text: string } | null
type BandRef = { name: string; slug: string }

type BandCard = {
  id: number
  name: string
  slug: string
  members_count: number
  recordings_count: number
  gigs_count: number
  in_amh: boolean
  image: Image
}

type PersonCard = {
  id: number
  name: string
  slug: string
  display_bands: BandRef[]
  gigs_count: number
  recordings_count: number
  image: Image
}

type VenueCard = {
  id: number
  name: string
  slug: string
  city?: string | null
  state?: string | null
  image: Image
}

type PersonDateCard = PersonCard & {
  birth_date: string | null
  birth_date_precision: string | null
  death_date: string | null
  death_date_precision: string | null
}

type Stats = {
  bands: number
  people: number
  recordings: number
  gigs: number
  venues: number
  chart_listings: number
}

const DEFAULT_STATS: Stats = { bands: 0, people: 0, recordings: 0, gigs: 0, venues: 0, chart_listings: 0 }

type Props = {
  stats: Stats
  recent_bands: BandCard[]
  recent_people: PersonCard[]
  recent_venues: VenueCard[]
  birthdays: PersonDateCard[]
  deaths: PersonDateCard[]
  og_image: string
}

const RELATIONSHIP_CHAIN = [
  { name: "William Shakespeare", href: "/people/john-cave-aka-william-shakespeare" },
  { name: "The Amazons",       href: "/bands/the-amazons" },
  { name: "Harry Brus",        href: "/people/harry-brus" },
  { name: "Australian Crawl",  href: "/bands/australian-crawl" },
  { name: "David Briggs",      href: "/people/david-briggs" },
  { name: "Little River Band", href: "/bands/little-river-band" },
  { name: "Derek Pellici",     href: "/people/derek-pellici" },
  { name: "Blackfeather",      href: "/bands/blackfeather" },
]

function formatDay(iso: string | null, precision: string | null): string {
  if (!iso) return ""
  const d = new Date(iso + "T00:00:00")
  if (precision === "year") return String(d.getFullYear())
  if (precision === "month") return d.toLocaleDateString("en-AU", { month: "long", year: "numeric" })
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "long" })
}

function bandList(bands: BandRef[]): string {
  if (bands.length === 0) return ""
  if (bands.length <= 2) return bands.map((b) => b.name).join(" & ")
  return bands
    .slice(0, 2)
    .map((b) => b.name)
    .join(", ") + ` & ${bands.length - 2} more`
}

function StatPill({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: number }) {
  return (
    <div className="flex flex-col items-center gap-1 px-4 py-4">
      <Icon className="w-5 h-5 text-amber-500 mb-0.5" />
      <span className="text-2xl font-bold text-stone-900 dark:text-white tabular-nums">{value.toLocaleString()}</span>
      <span className="text-xs text-stone-500 dark:text-stone-400 font-medium uppercase tracking-wide">{label}</span>
    </div>
  )
}

function SectionHeader({ title, href, linkLabel }: { title: string; href: string; linkLabel: string }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h2 className="text-base font-semibold text-stone-900 dark:text-white">{title}</h2>
      <Link href={href} className="text-xs font-medium text-amber-600 dark:text-amber-400 hover:underline no-underline flex items-center gap-1 shrink-0">
        {linkLabel} <ExternalLink className="w-3 h-3" />
      </Link>
    </div>
  )
}

function CardGrid({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
      {children}
    </div>
  )
}

/* ─── Band card (mirrors bands/Index.tsx BandCard) ─── */
function HomeBandCard({ band }: { band: BandCard }) {
  return (
    <Link
      href={`/bands/${band.slug}`}
      className="group block bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl overflow-hidden hover:border-amber-300 dark:hover:border-amber-700 hover:shadow-sm transition-all duration-150 no-underline"
    >
      <div className="aspect-square bg-stone-100 dark:bg-stone-800 relative overflow-hidden">
        {band.image ? (
          <img
            src={band.image.url}
            alt={band.image.alt_text}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Music2 className="w-12 h-12 text-stone-300 dark:text-stone-600" strokeWidth={0.75} />
          </div>
        )}
        {band.in_amh && (
          <span className="absolute top-2 left-2 text-xs font-medium text-amber-700 dark:text-amber-300 bg-white/90 dark:bg-stone-900/90 border border-amber-200 dark:border-amber-800 px-2 py-0.5 rounded-full leading-none">
            AMH
          </span>
        )}
      </div>
      <div className="p-3">
        <h3
          className="text-sm font-semibold text-stone-900 dark:text-white mb-1.5 group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors leading-snug line-clamp-2"
          style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
        >
          {band.name}
        </h3>
        <div
          className="flex items-center text-xs text-stone-400 dark:text-stone-500"
          style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}
        >
          <span>{band.members_count}m</span>
          <span className="mx-1 text-stone-300 dark:text-stone-700">·</span>
          <span>{band.recordings_count}r</span>
          <span className="mx-1 text-stone-300 dark:text-stone-700">·</span>
          <span>{band.gigs_count}g</span>
        </div>
      </div>
    </Link>
  )
}

/* ─── Person card (recently updated people) ─── */
function HomePersonCard({ person }: { person: PersonCard }) {
  const bands = bandList(person.display_bands)
  return (
    <Link
      href={`/people/${person.slug}`}
      className="group block bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl overflow-hidden hover:border-amber-300 dark:hover:border-amber-700 hover:shadow-sm transition-all duration-150 no-underline"
    >
      <div className="aspect-square bg-stone-100 dark:bg-stone-800 relative overflow-hidden">
        {person.image ? (
          <img
            src={person.image.url}
            alt={person.image.alt_text}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <User className="w-12 h-12 text-stone-300 dark:text-stone-600" strokeWidth={0.75} />
          </div>
        )}
      </div>
      <div className="p-3">
        <h3
          className="text-sm font-semibold text-stone-900 dark:text-white mb-1 group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors leading-snug line-clamp-2"
          style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
        >
          {person.name}
        </h3>
        {bands && (
          <p className="text-xs text-stone-400 dark:text-stone-500 truncate mb-1">{bands}</p>
        )}
        <div
          className="flex items-center text-xs text-stone-400 dark:text-stone-500"
          style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}
        >
          {person.recordings_count > 0 && (
            <>
              <span>{person.recordings_count}r</span>
              {person.gigs_count > 0 && <span className="mx-1 text-stone-300 dark:text-stone-700">·</span>}
            </>
          )}
          {person.gigs_count > 0 && <span>{person.gigs_count}g</span>}
        </div>
      </div>
    </Link>
  )
}

/* ─── Venue card ─── */
function HomeVenueCard({ venue }: { venue: VenueCard }) {
  const location = [ venue.city, venue.state ].filter(Boolean).join(", ")
  return (
    <Link
      href={`/venues/${venue.slug}`}
      className="group block bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl overflow-hidden hover:border-amber-300 dark:hover:border-amber-700 hover:shadow-sm transition-all duration-150 no-underline"
    >
      <div className="aspect-square bg-stone-100 dark:bg-stone-800 relative overflow-hidden">
        {venue.image ? (
          <img
            src={venue.image.url}
            alt={venue.image.alt_text}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <MapPin className="w-12 h-12 text-stone-300 dark:text-stone-600" strokeWidth={0.75} />
          </div>
        )}
      </div>
      <div className="p-3">
        <h3
          className="text-sm font-semibold text-stone-900 dark:text-white mb-1 group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors leading-snug line-clamp-2"
          style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
        >
          {venue.name}
        </h3>
        {location && (
          <p className="text-xs text-stone-400 dark:text-stone-500 truncate">{location}</p>
        )}
      </div>
    </Link>
  )
}

/* ─── Person date card (birthday / death) ─── */
function PersonDateCard({ person, dateKey, precisionKey }: {
  person: PersonDateCard
  dateKey: "birth_date" | "death_date"
  precisionKey: "birth_date_precision" | "death_date_precision"
}) {
  const bands = bandList(person.display_bands)
  const date  = formatDay(person[dateKey], person[precisionKey])
  return (
    <Link
      href={`/people/${person.slug}`}
      className="group block bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl overflow-hidden hover:border-amber-300 dark:hover:border-amber-700 hover:shadow-sm transition-all duration-150 no-underline"
    >
      <div className="aspect-square bg-stone-100 dark:bg-stone-800 relative overflow-hidden">
        {person.image ? (
          <img
            src={person.image.url}
            alt={person.image.alt_text}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <User className="w-12 h-12 text-stone-300 dark:text-stone-600" strokeWidth={0.75} />
          </div>
        )}
      </div>
      <div className="p-3">
        <h3
          className="text-sm font-semibold text-stone-900 dark:text-white mb-0.5 group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors leading-snug line-clamp-2"
          style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
        >
          {person.name}
        </h3>
        {bands && (
          <p className="text-xs text-stone-400 dark:text-stone-500 truncate mb-1">{bands}</p>
        )}
        {date && (
          <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">{date}</p>
        )}
      </div>
    </Link>
  )
}

export default function Home({
  stats = DEFAULT_STATS,
  recent_bands = [],
  recent_people = [],
  recent_venues = [],
  birthdays = [],
  deaths = [],
  og_image,
}: Partial<Props>) {
  const today = new Date()
  const monthName = today.toLocaleDateString("en-AU", { month: "long" })

  return (
    <>
      <Head title="Australian Music Database">
        <meta name="description" content="The Australian Music Database — discover Australian bands, musicians, recordings, gigs, and venues across the decades." />
        <meta property="og:title" content="Australian Music Database" />
        <meta property="og:description" content="The Australian Music Database — discover Australian bands, musicians, recordings, gigs, and venues across the decades." />
        {og_image && <meta property="og:image" content={og_image} />}
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Head>
      <PublicShell>

        {/* Hero */}
        <section className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 dark:text-white mb-2">
            Australian Music Database
          </h1>
          <p className="text-stone-500 dark:text-stone-400 max-w-2xl text-base leading-relaxed">
            A living archive of Australian bands, musicians, recordings, gigs, and venues — connecting the people and music that shaped Australian rock, pop, and beyond.
          </p>
        </section>

        {/* Stats */}
        <section className="mb-8 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-2xl overflow-hidden">
          <div className="grid grid-cols-3 sm:grid-cols-6 divide-x divide-y sm:divide-y-0 divide-stone-100 dark:divide-stone-800">
            <StatPill icon={Music2}      label="Bands"         value={stats.bands} />
            <StatPill icon={Users}       label="People"        value={stats.people} />
            <StatPill icon={Disc3}       label="Recordings"    value={stats.recordings} />
            <StatPill icon={CalendarDays} label="Gigs"         value={stats.gigs} />
            <StatPill icon={MapPin}      label="Venues"        value={stats.venues} />
            <StatPill icon={BarChart3}   label="Chart entries" value={stats.chart_listings} />
          </div>
        </section>

        {/* Relationship chain */}
        <section className="mb-8 bg-amber-50 dark:bg-amber-950/30 border border-amber-100 dark:border-amber-900/50 rounded-2xl px-5 py-4">
          <p className="text-xs font-semibold uppercase tracking-widest text-amber-600 dark:text-amber-400 mb-2">Six degrees of separation</p>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
            {RELATIONSHIP_CHAIN.map((item, i) => (
              <span key={item.href} className="flex items-center gap-2">
                <Link href={item.href} className="text-sm font-medium text-stone-800 dark:text-stone-200 hover:text-amber-600 dark:hover:text-amber-400 no-underline transition-colors">
                  {item.name}
                </Link>
                {i < RELATIONSHIP_CHAIN.length - 1 && (
                  <span className="text-stone-300 dark:text-stone-600 text-xs">→</span>
                )}
              </span>
            ))}
          </div>
          <p className="mt-2 text-xs text-stone-400 dark:text-stone-500">Every musician in the database is connected — explore the threads.</p>
        </section>

        {/* Born this month */}
        {birthdays.length > 0 && (
          <section className="mb-8">
            <SectionHeader title={`Born in ${monthName}`} href="/people" linkLabel="All people" />
            <CardGrid>
              {birthdays.map((p) => (
                <PersonDateCard key={p.id} person={p} dateKey="birth_date" precisionKey="birth_date_precision" />
              ))}
            </CardGrid>
          </section>
        )}

        {/* Passed this month */}
        {deaths.length > 0 && (
          <section className="mb-8">
            <SectionHeader title={`Passed in ${monthName}`} href="/people" linkLabel="All people" />
            <CardGrid>
              {deaths.map((p) => (
                <PersonDateCard key={p.id} person={p} dateKey="death_date" precisionKey="death_date_precision" />
              ))}
            </CardGrid>
          </section>
        )}

        {/* Recently updated bands */}
        {recent_bands.length > 0 && (
          <section className="mb-8">
            <SectionHeader title="Recently updated bands" href="/bands" linkLabel="All bands" />
            <CardGrid>
              {recent_bands.map((b) => (
                <HomeBandCard key={b.id} band={b} />
              ))}
            </CardGrid>
          </section>
        )}

        {/* Recently updated people */}
        {recent_people.length > 0 && (
          <section className="mb-8">
            <SectionHeader title="Recently updated people" href="/people" linkLabel="All people" />
            <CardGrid>
              {recent_people.map((p) => (
                <HomePersonCard key={p.id} person={p} />
              ))}
            </CardGrid>
          </section>
        )}

        {/* Recently updated venues */}
        {recent_venues.length > 0 && (
          <section className="mb-8">
            <SectionHeader title="Recently updated venues" href="/venues" linkLabel="All venues" />
            <CardGrid>
              {recent_venues.map((v) => (
                <HomeVenueCard key={v.id} venue={v} />
              ))}
            </CardGrid>
          </section>
        )}

      </PublicShell>
    </>
  )
}
